const { weekStart, shiftDay } = require("../mobile/dayNav.js");

function createWeekCache({ ttlMs = 60000, now = () => Date.now() } = {}) {
  /** @type {Map<string, { fetchedAt: number, from: string, to: string, days: Map<string, object> }>} */
  const weeks = new Map();
  /** @type {Map<string, number>} */
  const revisions = new Map();
  /** @type {Map<string, object>} full day docs from fetch or write-through patch */
  const dayDocs = new Map();

  function trackDoc(doc) {
    if (!doc || doc.docId == null) return;
    const id = String(doc.docId);
    dayDocs.set(id, doc);
    if (typeof doc.revision === "number") {
      revisions.set(id, doc.revision);
    }
  }

  function indexDocuments(from, to, documents) {
    const days = new Map();
    // Sparse server responses: missing calendar days mean empty lists
    let cursor = from;
    while (cursor <= to) {
      const empty = { docId: cursor, payload: [], revision: 0 };
      days.set(cursor, empty);
      trackDoc(empty);
      cursor = shiftDay(cursor, 1);
    }
    for (const doc of documents) {
      if (!doc || doc.docId == null) continue;
      const docId = String(doc.docId);
      const normalized = { ...doc, docId };
      days.set(docId, normalized);
      trackDoc(normalized);
    }
    const entry = { fetchedAt: now(), from, to, days };
    weeks.set(from, entry);
    return entry;
  }

  async function ensureWeek({ dayId, weekStartsOnMonday = true, fetch, force = false }) {
    const from = weekStart(dayId, weekStartsOnMonday);
    const to = shiftDay(from, 6);
    const existing = weeks.get(from);
    if (existing && !force && now() - existing.fetchedAt < ttlMs) {
      return existing;
    }
    const result = await fetch(from, to);
    const documents = Array.isArray(result.documents) ? result.documents : [];
    return indexDocuments(from, to, documents);
  }

  function getDay(dayId) {
    const id = String(dayId);
    for (const entry of weeks.values()) {
      if (id >= entry.from && id <= entry.to) {
        const doc = entry.days.get(id);
        if (doc) return doc;
      }
    }
    const cached = dayDocs.get(id);
    if (cached) return cached;
    return { docId: id, payload: [] };
  }

  function patchDay(docId, doc) {
    const id = String(docId);
    const merged = { ...doc, docId: doc.docId != null ? String(doc.docId) : id };
    trackDoc(merged);
    for (const entry of weeks.values()) {
      if (id >= entry.from && id <= entry.to) {
        entry.days.set(id, merged);
      }
    }
    return merged;
  }

  function getRevision(docId) {
    return revisions.get(String(docId));
  }

  function hasWeek(dayId, weekStartsOnMonday = true) {
    const from = weekStart(dayId, weekStartsOnMonday);
    return weeks.has(from);
  }

  function clear() {
    weeks.clear();
    revisions.clear();
    dayDocs.clear();
  }

  return { ensureWeek, getDay, patchDay, getRevision, hasWeek, clear };
}

module.exports = { createWeekCache };
